import { ModuleLabel } from './module-label';
import { MarkdownText } from './markdown-text';
import 'primeflex/primeflex.css';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { LearningItemDto, moduleStatusToString, MyModuleCourseDto, MyModuleDto } from '../general/dtos';
import { Column } from 'primereact/column';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { GeneralErrorDialog } from './dialogs/error-dialog';
import { FilterMatchMode } from "primereact/api";
import { ViewModuleDialog } from './dialogs/view-module-dialog';
import { Card } from 'primereact/card';
import { UserHomeDataContext, UserHomeDataContextValue } from '../providers/user-home-provider';
import { UserPromptDialog } from './dialogs/prompt-dialog';
import Config from '../general/config';
import { LearningPathViewer } from './learning-path-viewer';
import { useAuth } from '../providers/auth-provider';
import { ViewManagedModuleDialog } from './dialogs/view-managed-module-dialog';
import { Panel } from 'primereact/panel';
import { ModuleUseNotice } from './dialogs/module-use-notice';
import { PublicHome } from './public-home';
import { useBookmarkSelection } from '../general/bookmark-selection';
import { completionOpportunities, CourseCompletionDialog, CourseCompletionOpportunity } from './dialogs/course-completion-dialog';
import { defaultCompletionEmail } from '../general/email';
import '../assets/theme-content.css';
import { useUserStore } from './zustand-stores';
export default function UserHomePage() {
  const ctx: UserHomeDataContextValue | null = useContext<UserHomeDataContextValue | null>(UserHomeDataContext);
  const { setIsAuthenticated, 
    learningPath, learningPathError, learningPathLoading,
    allUserModules, allUserModulesError, allUserModulesLoading,
    fullModule, fullModuleError, fullModuleLoading, loadFullModule, isModuleCompleted,
    endSession, createSessionEvent, disregardSession, loadLearningPath } = ctx!;

  const auth = useAuth();
  const isLoading: boolean = auth.getIsLoading();
  const isAuthenticated: boolean = auth.getIsAuthenticated();
  const identity = isAuthenticated ? auth.getUser?.()?.sub ?? '' : '';
  const profile = useUserStore(state => state.user);
  const learnerName = (profile?.sub === identity ? profile.first_name : '') || auth.getUser?.()?.given_name || '';
  const previousIdentity = useRef(identity);
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;

  const [error, setError] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');

  const [moduleDialogVisible, setModuleDialogVisible] = useState<boolean>(false);
  const [managedModuleDialogVisible, setManagedModuleDialogVisible] = useState<boolean>(false);
  const [restartLessonKey, setRestartLessonKey] = useState<string | null>(null);
  const [viewedResumeAvailability, setViewedResumeAvailability] = useState<Record<string, boolean>>({});
  // Keep dashboard reads tied to progress changes, rather than unrelated dialog renders.
  const displayedLearningPath = useMemo(() => ({ ...learningPath,
    inProgress: learningPath?.inProgress?.map(item => Object.prototype.hasOwnProperty.call(viewedResumeAvailability, item.key)
      ? { ...item, can_resume: viewedResumeAvailability[item.key] } : item) ?? []
  }), [learningPath, viewedResumeAvailability]);
  const [noticeModuleKey, setNoticeModuleKey] = useState<string | null>(null);
  const opening = useRef(false);
  const [bookmarkedLesson, setBookmarkedLesson] = useBookmarkSelection('module_key');
  const requestedLesson = useRef<{ key: string | null; identity: string } | null>(null);
  const openedLesson = useRef<string | null>(null);

  const [markSessionCompletePrompt, setMarkSessionCompletePrompt] = useState<string>('');
  const [completionSaving, setCompletionSaving] = useState(false);
  const completing = useRef(false);
  const completionGeneration = useRef(0);
  const [completion, setCompletion] = useState<{ sessionId: string; courses: CourseCompletionOpportunity[] } | null>(null);

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    created_at: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  useEffect(() => {
    if (learningPathError) {
        setError(learningPathError);
        setErrorTitle('Error loading your lessons');
        setErrorDetails('Could not load your lessons. Please try again later or contact support.');
    }
    if (allUserModulesError) {
        setError(allUserModulesError);
        setErrorTitle('Error loading your lesson list');
        setErrorDetails('Could not load your lesson list. Please try again later or contact support.');
    }
    if (fullModuleError) {
        setError(fullModuleError);
        setErrorTitle('Error loading lesson content');
        setErrorDetails('Could not load the lesson content. Please try again later or contact support.');
    }
  }, [learningPathError, allUserModulesError, fullModuleError]);

  function useIsMobile(breakpoint: number = 960): boolean {
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= breakpoint);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, [breakpoint]);

    return isMobile;
  }

  const isMobile: boolean = useIsMobile();

  const openApprovedModule = async (module_key: string): Promise<void> => {
    const expectedIdentity = identity;
    const expectedRequest = requestedLesson.current;
    await loadFullModule(module_key, true)
    .then((data: MyModuleDto | null) => {
            if (currentIdentity.current !== expectedIdentity || requestedLesson.current !== expectedRequest) return;
            if (data && (data.module_key ?? '').length > 0) {
                openedLesson.current = module_key;
                if (data.is_managed) {
                    setManagedModuleDialogVisible(true);
                } else {
                    setModuleDialogVisible(true);
                }
            } else {
                setModuleDialogVisible(false);
                setManagedModuleDialogVisible(false);
                setError(`Lesson '${module_key}' does not exist or you do not have permission to access it.`);
                setErrorTitle('Lesson unavailable');
                setErrorDetails('The lesson may have been deleted, may not be assigned to you, or the lesson link may be invalid.');
                setBookmarkedLesson(null, true);
            }
        }
    );
    if (currentIdentity.current === expectedIdentity && requestedLesson.current === expectedRequest) { setNoticeModuleKey(null); opening.current = false; }
  };

  const loadUserModuleContent = async (moduleKey: string, startFromBeginning = false): Promise<void> => {
    if (opening.current || !isAuthenticated) return;
    setRestartLessonKey(startFromBeginning ? moduleKey : null);
    setBookmarkedLesson(moduleKey);
  };
  const cancelNotice = () => {
    setRestartLessonKey(null);
    setNoticeModuleKey(null); opening.current = false;
    setBookmarkedLesson(null, true);
  };
  useEffect(() => {
    if (previousIdentity.current !== identity) {
        setRestartLessonKey(null);
        setViewedResumeAvailability({});
        if (previousIdentity.current) setBookmarkedLesson(null, true);
        previousIdentity.current = identity;
        setNoticeModuleKey(null); opening.current = false;
        setModuleDialogVisible(false); setManagedModuleDialogVisible(false);
        setCompletion(null);
        completionGeneration.current++;
    }
  }, [identity, setBookmarkedLesson]);

  const onHtmlMessage = async (kind: string, data: any): Promise<void> => {
    const payload: string = typeof data === "object" ? JSON.stringify(data) : String(data);
    createSessionEvent(payload);
  }

  const completeLesson = async (): Promise<void> => {
    if (completing.current) return;
    completing.current = true;
    setCompletionSaving(true);
    const expectedIdentity = identity;
    const expectedGeneration = completionGeneration.current;
    try {
        const completed = await endSession();
        if (currentIdentity.current !== expectedIdentity || expectedGeneration !== completionGeneration.current) return;
        if (!completed) {
            setErrorTitle('Could not mark the lesson complete');
            setError('Please try again. Your feedback will be requested after completion is saved.');
            setErrorDetails('');
            return;
        }
        setMarkSessionCompletePrompt('');
        const courses = completionOpportunities(completed.certificate_names, completed.completion_email_courses, completed.feedback_courses);
        setCompletion(courses.length ? { sessionId: completed.session_id, courses } : null);
    } finally {
        completing.current = false;
        setCompletionSaving(false);
    }
  };

  const onCloseModuleDialog = (): void => {
    setRestartLessonKey(null);
    openedLesson.current = null;
    setBookmarkedLesson(null);

    setModuleDialogVisible(false);
    setManagedModuleDialogVisible(false);
    if (!isModuleCompleted) {
        setMarkSessionCompletePrompt(`Would you like to mark the lesson "${fullModule.name}" complete? It will move from Started to Completed on your dashboard.`);
    } else {
        void completeLesson();
    }
  };

  useEffect(() => {
    setIsAuthenticated(isAuthenticated);
  }, [isAuthenticated, setIsAuthenticated]);

  useEffect(() => {
    const previous = requestedLesson.current;
    if (previous?.key === bookmarkedLesson && previous.identity === identity) return;
    requestedLesson.current = { key: bookmarkedLesson, identity };
    if (previous) {
        if (openedLesson.current && isModuleCompleted) void endSession();
        else if (openedLesson.current || opening.current) void disregardSession();
    }
    openedLesson.current = null;
    setModuleDialogVisible(false);
    setManagedModuleDialogVisible(false);
    setNoticeModuleKey(null);
    if (bookmarkedLesson || previous?.identity !== identity) setMarkSessionCompletePrompt('');
    opening.current = false;
    if (isAuthenticated && bookmarkedLesson) {
        completionGeneration.current++;
        setCompletion(null);
        opening.current = true;
        setNoticeModuleKey(bookmarkedLesson);
    }
    // Only a changed bookmark or identity opens/closes a lesson, not provider refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmarkedLesson, identity, isAuthenticated]);

  const viewButtonTemplate = (row: MyModuleDto) => (
    <Button
      label="View"
      icon="pi pi-eye"
      className="p-button-text p-button-sm"
      disabled={!!noticeModuleKey}
      onClick={() => {
        loadUserModuleContent(row.module_key);
      }}
    />
  );

  if (isLoading) {
    return (<><div className="flex flex-column justify-content-center align-items-center h-screen gap-3">
      <ProgressSpinner />
      <span className="text-xl text-primary">Loading your experience...</span>
    </div></>);
  }

  const descriptionBodyTemplate = (row: MyModuleDto) => {
    return <MarkdownText text={row.description} style={{ maxHeight: '8rem', overflowY: 'auto' }} />;
  };

  const coursesBodyTemplate = (row: MyModuleDto) => {
    const courses: MyModuleCourseDto[] = (row.courses ?? []).filter(c => c.key.length > 0);
    return (<><ul>
        {courses.map(c => <><li>{c.name}</li></>)}
    </ul></>);
  };

  const statusBodyTemplate = (row: MyModuleDto) => {
    return (<><span>{moduleStatusToString(row.status)}</span></>);
  };

  const renderAllModulesContent = () => {
    return (<><Panel header='All assigned modules (not shown in production)' toggleable={true} collapsed={true} className="shadow-2">
        <div className="flex flex-column p-4">
            <h2 className="mb-3">All my modules</h2>

            <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
                {!isMobile && (<>
                <DataTable
                    value={allUserModules}
                    loading={allUserModulesLoading}
                    dataKey="key"
                    scrollable
                    scrollHeight="flex"
                    style={{ height: "100%" }}
                    rowClassName={(row) => (row.is_new ? "new-assignment-row" : "")}

                    filters={filters}
                    onFilter={(e) => setFilters(e.filters)}
                    filterDisplay="menu"

                    responsiveLayout="stack"
                    breakpoint="960px"

                    emptyMessage={<div className="flex align-items-center gap-2"><i className="pi pi-info-circle" /><span>You do not have any active assignments at the moment. Modules assigned to you by the administrator will appear here.</span></div>}
                    >
                    <Column field="name" header="Module" body={(module: MyModuleDto) => <ModuleLabel name={module.name} managed={module.is_managed} />} sortable filter filterPlaceholder="Search..." filterMatchMode={FilterMatchMode.CONTAINS} style={{ whiteSpace: "normal" }}/>
                    <Column field="description" body={descriptionBodyTemplate} header="Description" sortable style={{ whiteSpace: "normal" }} />
                    <Column field="created_at" header="Assigned on" sortable filter filterPlaceholder="Search..." filterMatchMode={FilterMatchMode.CONTAINS} style={{ whiteSpace: "normal" }} />
                    <Column field="courses" body={coursesBodyTemplate} header="Via course(s)" style={{ whiteSpace: "normal" }} />
                    <Column field="status" body={statusBodyTemplate} sortable header="Status" />
                    <Column header="Actions" body={viewButtonTemplate} style={{ whiteSpace: "normal" }} />
                </DataTable>
                </>)}

                {isMobile && (<>
                    <div className="flex flex-column gap-3 p-2">
                        {allUserModules.map((m) => (
                            <Card
                                header={<div className="text-lg font-bold"><ModuleLabel name={m.name} managed={m.is_managed} /></div>}
                                footer={viewButtonTemplate(m)}
                                >
                                <div className="text-sm text-secondary">
                                    {(m.description ?? '').length > 0 ? descriptionBodyTemplate(m) : '-- no description available --'}
                                </div>
                            </Card>
                        ))}
                    </div>
                </>)}
            </div>
        </div>
    </Panel></>);
  }

  return (<>
      {isAuthenticated ? (<>
        <div className="user-home-content theme-content" style={{ height: "100vh", overflowY: "auto", overflowX: "auto", display: "flex", flexDirection: "column" }}>
            <LearningPathViewer 
                learnerName={learnerName}
                learningPath={displayedLearningPath}
                enableViewButton={!noticeModuleKey && !completionSaving && !completion && !markSessionCompletePrompt}
                loading={learningPathLoading}
                isMobile={isMobile}
                onView={(item: LearningItemDto, startFromBeginning?: boolean) => {
                    loadUserModuleContent(item.key, startFromBeginning);
                }}
                isImpersonating={false}
            />

            {Config.env === 'local' && renderAllModulesContent()}

            <ViewModuleDialog 
                isReviewMode={isModuleCompleted}
                contentLoading={fullModuleLoading}
                fullModule={fullModule}
                onClose={onCloseModuleDialog}
                visible={moduleDialogVisible}
                onMessage={onHtmlMessage}
            />

            <ViewManagedModuleDialog 
                startFromBeginning={restartLessonKey === fullModule?.module_key}
                onPositionViewed={fragmentKey => {
                    const key = fullModule?.module_key;
                    if (!key) return;
                    const canResume = fragmentKey !== fullModule.fragments?.[0]?.key;
                    setViewedResumeAvailability(current => current[key] === canResume ? current : { ...current, [key]: canResume });
                }}
                isReviewMode={isModuleCompleted}
                contentLoading={fullModuleLoading}
                fullModule={fullModule}
                onClose={onCloseModuleDialog}
                visible={managedModuleDialogVisible}
                isTestMode={false}
                onMessage={onHtmlMessage}
            />
        </div>
        </>) : (<>
            <PublicHome />

        </>)}

    <ModuleUseNotice moduleKey={noticeModuleKey} userKey={isAuthenticated ? auth.getUser?.()?.sub ?? '' : ''}
        onApproved={openApprovedModule} onCancel={cancelNotice} />
    <GeneralErrorDialog
        visible={error.length > 0}
        title={errorTitle}
        message={error}
        details={errorDetails}
        onClose={() => setError('')}
    />

    <UserPromptDialog
          visible={markSessionCompletePrompt.length > 0}
          title={'Are you done with this lesson?'}
          question={markSessionCompletePrompt}
          onClose={() => { if (!completionSaving) { void disregardSession(); setMarkSessionCompletePrompt(''); } }}
          actions={[
            <Button label="Yes" icon="pi pi-thumbs-up" className="p-button-primary" loading={completionSaving} disabled={completionSaving} onClick={(e) => {
                void completeLesson();
            }} />,
            <Button label="No" icon="pi pi-thumbs-down" className="p-button-outlined" disabled={completionSaving} onClick={(e) => {
                disregardSession();
                setMarkSessionCompletePrompt('');
            }} />
          ]}
    />
    {completion && !bookmarkedLesson && !moduleDialogVisible && !managedModuleDialogVisible
        && !noticeModuleKey && !markSessionCompletePrompt && !completionSaving && <CourseCompletionDialog
        key={`${identity}:${completion.sessionId}:${completion.courses[0].course.course_key}`}
        opportunity={completion.courses[0]} sessionId={completion.sessionId} remaining={completion.courses.length}
        defaultEmail={defaultCompletionEmail(auth.getUser?.())} onCertificateConfirmed={() => { void loadLearningPath?.(); }}
        onDone={() => setCompletion(current => current?.sessionId !== completion.sessionId ? current
            : current.courses.length > 1 ? { ...current, courses: current.courses.slice(1) } : null)} />}
  </>)
};
